/**
 * Check-Out Reflection Modal
 * 
 * A multi-step wizard for daily/session reflection on time usage and value creation.
 * Implements WCAG 2.1 AA accessibility standards and mobile-first design.
 * 
 * @module components/timeTracking/CheckOutReflectionModal
 * @author SuperKonnected Team - Senior UI/UX Implementation
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { timeTrackingService } from '../../services/timeTrackingService';
import { SessionReflection } from '../../types/timeTracking';
import { X, ChevronLeft, ChevronRight, Check } from '../icons';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface CheckOutReflectionModalProps {
  userId: string;
  userProfile: any;
  sessionDuration: number;
  onClose: () => void;
  onComplete: () => void;
}

interface QuestionConfig {
  id: keyof SessionReflection['questions'];
  question: string;
  placeholder: string;
  type: 'textarea' | 'select' | 'emoji';
  options?: Array<{ value: string; emoji?: string; label: string }>;
  required?: boolean;
}

type FeelingType = 'productive' | 'neutral' | 'distracted';

// ============================================================================
// CONSTANTS
// ============================================================================

const QUESTIONS: QuestionConfig[] = [
  {
    id: 'mainActivity',
    question: 'Wat heb je vooral gedaan vandaag?',
    placeholder: 'Bijv: Netwerken, meetings gepland, content gemaakt...',
    type: 'textarea',
    required: true
  },
  {
    id: 'accomplishments',
    question: 'Wat heb je bereikt?',
    placeholder: 'Wat zijn je concrete resultaten vandaag?',
    type: 'textarea',
    required: true
  },
  {
    id: 'goalContribution',
    question: 'Welk hoofddoel heb je hiermee gediend?',
    placeholder: 'Kies uit je doelstellingen...',
    type: 'select',
    required: true
  },
  {
    id: 'feeling',
    question: 'Hoe voel je je over je productiviteit?',
    type: 'emoji',
    placeholder: '',
    options: [
      { value: 'productive', emoji: '🎯', label: 'Productief' },
      { value: 'neutral', emoji: '😐', label: 'Neutraal' },
      { value: 'distracted', emoji: '😵', label: 'Afgeleid' }
    ],
    required: true
  },
  {
    id: 'nextFocus',
    question: 'Waar ga je morgen op focussen?',
    placeholder: 'Je prioriteit voor morgen...',
    type: 'textarea',
    required: false
  }
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CheckOutReflectionModal: React.FC<CheckOutReflectionModalProps> = ({
  userId,
  userProfile,
  sessionDuration,
  onClose,
  onComplete
}) => {
  // ============================================================================
  // STATE
  // ============================================================================

  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState<SessionReflection['questions']>({
    mainActivity: '',
    accomplishments: '',
    goalContribution: '',
    feeling: 'neutral',
    nextFocus: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for focus management
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Focus management (accessibility)
  useEffect(() => {
    // Store previous focus
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus first element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Restore focus on unmount
    return () => {
      previousFocusRef.current?.focus();
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'Enter' && e.ctrlKey) {
        // Ctrl+Enter to submit
        if (step === QUESTIONS.length) {
          handleSubmit();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    onClose();
  }, [isSubmitting, onClose]);

  const handleNext = useCallback(() => {
    const currentQuestion = QUESTIONS[step - 1];
    const answer = answers[currentQuestion.id];

    // Validate required fields
    if (currentQuestion.required && !answer) {
      setError('Dit veld is verplicht');
      return;
    }

    setError(null);
    setStep(prev => Math.min(prev + 1, QUESTIONS.length));
  }, [step, answers]);

  const handlePrevious = useCallback(() => {
    setError(null);
    setStep(prev => Math.max(prev - 1, 1));
  }, []);

  const handleAnswerChange = useCallback(
    (questionId: keyof SessionReflection['questions'], value: string) => {
      setAnswers(prev => ({ ...prev, [questionId]: value }));
      setError(null);
    },
    []
  );

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Get time breakdown
      const weeklyBreakdown = await timeTrackingService.getWeeklyBreakdown(userId);
      const categoryTotals = await timeTrackingService.getCategoryTotals(userId);

      // Map to time breakdown
      const timeBreakdown = {
        work: categoryTotals.work * 3600,
        personal: categoryTotals.personal * 3600,
        self: categoryTotals.self * 3600,
        essential: categoryTotals.essential * 3600
      };

      // Create reflection document
      const reflection: Omit<SessionReflection, 'id'> = {
        userId,
        sessionDate: Timestamp.now(),
        duration: sessionDuration,
        questions: answers,
        timeBreakdown,
        goalMapping: mapTimeToGoals(weeklyBreakdown, answers.goalContribution),
        createdAt: Timestamp.now()
      };

      // Save to Firestore
      await addDoc(collection(db, 'sessionReflections'), reflection);

      console.log('✅ Session reflection saved');
      onComplete();
    } catch (err) {
      console.error('❌ Error saving reflection:', err);
      setError('Er ging iets mis bij het opslaan. Probeer het opnieuw.');
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  const renderQuestion = () => {
    const currentQuestion = QUESTIONS[step - 1];
    const currentAnswer = answers[currentQuestion.id];

    switch (currentQuestion.type) {
      case 'textarea':
        return (
          <textarea
            value={currentAnswer}
            onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
            placeholder={currentQuestion.placeholder}
            className="w-full p-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none transition-all"
            rows={4}
            aria-label={currentQuestion.question}
            aria-required={currentQuestion.required}
            autoFocus
          />
        );

      case 'select':
        const goals = getUserGoals(userProfile);
        return (
          <select
            value={currentAnswer}
            onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
            className="w-full p-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-base"
            aria-label={currentQuestion.question}
            aria-required={currentQuestion.required}
            autoFocus
          >
            <option value="">Kies een doel...</option>
            {goals.map((goal) => (
              <option key={goal} value={goal}>
                {goal}
              </option>
            ))}
          </select>
        );

      case 'emoji':
        return (
          <div 
            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
            role="radiogroup"
            aria-label={currentQuestion.question}
          >
            {currentQuestion.options?.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswerChange(currentQuestion.id, option.value)}
                className={`
                  p-6 border-2 rounded-xl transition-all min-h-[100px] sm:min-h-[120px]
                  flex flex-col items-center justify-center gap-3
                  hover:border-orange-300 hover:bg-orange-50
                  focus:ring-2 focus:ring-orange-500 focus:outline-none
                  ${
                    currentAnswer === option.value
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-200 bg-white'
                  }
                `}
                role="radio"
                aria-checked={currentAnswer === option.value}
                aria-label={option.label}
                type="button"
              >
                <span className="text-5xl sm:text-6xl" role="img" aria-hidden="true">
                  {option.emoji}
                </span>
                <span className="font-medium text-gray-700 text-base sm:text-lg">
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="checkout-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6">
          <button
            ref={firstFocusableRef}
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20 transition-colors focus:ring-2 focus:ring-white focus:outline-none"
            aria-label="Sluit reflectie"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>

          <h2 id="checkout-title" className="text-2xl font-bold mb-2">
            💭 Dagelijkse Reflectie
          </h2>
          <p className="text-white/90 text-sm">
            Neem even de tijd om terug te kijken op je dag
          </p>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-100 px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Vraag {step} van {QUESTIONS.length}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round((step / QUESTIONS.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${(step / QUESTIONS.length) * 100}%` }}
              role="progressbar"
              aria-valuenow={step}
              aria-valuemin={1}
              aria-valuemax={QUESTIONS.length}
            />
          </div>
        </div>

        {/* Question */}
        <div className="p-6 sm:p-8">
          <div className="mb-6">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
              {QUESTIONS[step - 1].question}
            </h3>

            {renderQuestion()}

            {error && (
              <div
                className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                role="alert"
              >
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3 px-6 pb-6">
          <button
            onClick={step > 1 ? handlePrevious : handleClose}
            className="flex items-center gap-2 px-4 py-3 text-gray-600 hover:text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors focus:ring-2 focus:ring-orange-500 focus:outline-none min-h-[44px]"
            disabled={isSubmitting}
            type="button"
          >
            {step > 1 ? (
              <>
                <ChevronLeft size={20} />
                <span>Vorige</span>
              </>
            ) : (
              <span>Overslaan</span>
            )}
          </button>

          <button
            onClick={step < QUESTIONS.length ? handleNext : handleSubmit}
            className={`
              flex items-center gap-2 px-6 py-3 font-medium rounded-lg
              transition-all focus:ring-2 focus:ring-orange-500 focus:outline-none
              min-h-[44px] min-w-[120px] justify-center
              ${
                isSubmitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-lg'
              }
            `}
            disabled={isSubmitting}
            type="button"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Opslaan...</span>
              </>
            ) : step < QUESTIONS.length ? (
              <>
                <span>Volgende</span>
                <ChevronRight size={20} />
              </>
            ) : (
              <>
                <Check size={20} />
                <span>Voltooien</span>
              </>
            )}
          </button>
        </div>

        {/* Helper text */}
        <div className="px-6 pb-4 text-center">
          <p className="text-xs text-gray-500">
            Druk <kbd className="px-2 py-1 bg-gray-100 rounded border text-gray-700">Esc</kbd> om te sluiten
            {step < QUESTIONS.length && (
              <> of <kbd className="px-2 py-1 bg-gray-100 rounded border text-gray-700">Ctrl+Enter</kbd> voor volgende</>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extract user goals from profile
 */
function getUserGoals(userProfile: any): string[] {
  const goals = userProfile?.goals || {};
  const goalList: string[] = [];

  if (goals.clients) goalList.push('Nieuwe klanten werven');
  if (goals.partnerships) goalList.push('Partnerships sluiten');
  if (goals.hiring) goalList.push('Team uitbreiden');
  if (goals.funding) goalList.push('Financiering vinden');
  if (goals.speaking) goalList.push('Speaking opportunities');
  if (goals.content) goalList.push('Content creëren');
  if (goals.personalBrand) goalList.push('Personal brand bouwen');
  if (goals.productLaunch) goalList.push('Product lanceren');
  if (goals.community) goalList.push('Community bouwen');

  return goalList.length > 0 ? goalList : ['Netwerken', 'Leren', 'Groeien'];
}

/**
 * Map time breakdown to goals
 */
function mapTimeToGoals(timeBreakdown: any, selectedGoal: string): any {
  const mapping: any = {};

  // Simpele mapping - kan later uitgebreid worden met AI
  Object.entries(timeBreakdown).forEach(([key, seconds]) => {
    const [category, activity] = key.split('_');

    if (category === 'work' && activity === 'netwerken') {
      mapping['Netwerken'] = (mapping['Netwerken'] || 0) + (seconds as number);
    } else if (category === 'work' && activity === 'planning') {
      mapping['Planning'] = (mapping['Planning'] || 0) + (seconds as number);
    }
  });

  if (selectedGoal) {
    mapping[selectedGoal] = (mapping[selectedGoal] || 0) + 3600; // Add 1 hour to selected goal
  }

  return mapping;
}

export default CheckOutReflectionModal;
















